import React, { useState, useEffect } from 'react';

const GuestManager = ({ seminar, onSave }) => {
    const [guestList, setGuestList] = useState(seminar.guests || []);
    const [groups, setGroups] = useState(seminar.groups || []);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#cccccc');
    const [currentFilter, setCurrentFilter] = useState('non envoyé');
    const [emailInput, setEmailInput] = useState('');
    const [selectedGuests, setSelectedGuests] = useState([]);

    const filteredList = guestList.filter(guest => guest.status === currentFilter);

    useEffect(() => {
        setSelectedGuests([]);
    }, [currentFilter]);

    const handleSelectGuest = (email) => {
        setSelectedGuests(prev => 
            prev.includes(email) 
                ? prev.filter(gEmail => gEmail !== email) 
                : [...prev, email]
        );
    };

    const handleSelectAll = () => {
        if (selectedGuests.length === filteredList.length) {
            setSelectedGuests([]);
        } else {
            setSelectedGuests(filteredList.map(g => g.email));
        }
    };

    const handleBulkAssignGroup = (groupId) => {
        const updatedGuests = guestList.map(guest => 
            selectedGuests.includes(guest.email) 
                ? { ...guest, groupId } 
                : guest
        );
        setGuestList(updatedGuests);
        onSave({ ...seminar, guests: updatedGuests });
        setSelectedGuests([]);
    };

    const handleBulkDelete = () => {
        const updatedGuestList = guestList.filter(guest => !selectedGuests.includes(guest.email));
        setGuestList(updatedGuestList);
        onSave({ ...seminar, guests: updatedGuestList });
        setSelectedGuests([]);
    };

    const handleAddGroup = () => {
        if (newGroupName.trim() && !groups.some(g => g.name === newGroupName.trim())) {
            const newGroup = { id: `group-${Date.now()}`, name: newGroupName.trim(), color: newGroupColor };
            const updatedGroups = [...groups, newGroup];
            setGroups(updatedGroups);
            onSave({ ...seminar, groups: updatedGroups });
            setNewGroupName('');
            setNewGroupColor('#cccccc');
        }
    };

    const handleGroupColorChange = (groupId, newColor) => {
        const updatedGroups = groups.map(g => g.id === groupId ? { ...g, color: newColor } : g);
        setGroups(updatedGroups);
        onSave({ ...seminar, groups: updatedGroups });
    }

    const handleDeleteGroup = (groupIdToDelete) => {
        const updatedGroups = groups.filter(g => g.id !== groupIdToDelete);
        setGroups(updatedGroups);
        const updatedGuests = guestList.map(guest => 
            guest.groupId === groupIdToDelete ? { ...guest, groupId: '' } : guest
        );
        setGuestList(updatedGuests);
        onSave({ ...seminar, groups: updatedGroups, guests: updatedGuests });
    };

    const handleAssignGroup = (guestEmail, groupId) => {
        const updatedGuests = guestList.map(guest => 
            guest.email === guestEmail ? { ...guest, groupId } : guest
        );
        setGuestList(updatedGuests);
        onSave({ ...seminar, guests: updatedGuests });
    };

    const addGuests = (emails) => {
        const newGuests = emails
            .map(email => email.trim())
            .filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            .filter(email => !guestList.some(g => g.email === email))
            .map(email => ({ nom: '', prenom: '', email: email, status: 'non envoyé', groupId: '', fonction: '' }));
        
        if (newGuests.length > 0) {
            const updatedGuestList = [...guestList, ...newGuests];
            setGuestList(updatedGuestList);
            onSave({ ...seminar, guests: updatedGuestList });
        }
    };
    
    const handleDeleteGuest = (emailToDelete) => {
        const updatedGuestList = guestList.filter(guest => guest.email !== emailToDelete);
        setGuestList(updatedGuestList);
        onSave({ ...seminar, guests: updatedGuestList });
    };

    const handleValidateClick = () => {
        const emails = emailInput.split(/[\n,;]+/).filter(e => e.trim() !== '');
        if (emails.length > 0) { addGuests(emails); setEmailInput(''); }
    };

    const handleCsvUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const emails = text.split(/[\n,;]+/).map(line => line.trim());
                addGuests(emails);
            };
            reader.readAsText(file);
        }
    };
    
    const handleActionClick = () => {
        const guestsToActOn = guestList.filter(g => g.status === currentFilter);
        if (guestsToActOn.length === 0) {
            alert(`Aucun invité dans la catégorie "${currentFilter}".`);
            return;
        }

        const emails = guestsToActOn.map(g => g.email).join(',');
        let subject = '';
        let body = '';
        const registrationLink = `${window.location.origin}${window.location.pathname}?view=register&seminarId=${seminar.id}`;

        switch (currentFilter) {
            case 'non envoyé':
                subject = `Invitation au séminaire: ${seminar.name}`;
                body = `Bonjour,\n\nVous êtes invité(e) à notre prochain séminaire "${seminar.name}".\n\nVeuillez cliquer sur le lien ci-dessous pour confirmer votre présence et obtenir plus de détails.\n\n${registrationLink}\n\nCordialement,`;
                break;
            case 'en attente':
                subject = `Relance de l'invitation au séminaire: ${seminar.name}`;
                body = `Bonjour,\n\nCeci est un petit rappel concernant votre invitation pour le séminaire "${seminar.name}". Pourriez-vous nous confirmer votre présence ?\n\n${registrationLink}\n\nCordialement,`;
                break;
            case 'inscrit':
                subject = `Rappel du séminaire: ${seminar.name}`;
                body = `Bonjour,\n\nJuste un petit rappel pour notre séminaire "${seminar.name}" qui approche à grands pas. Nous avons hâte de vous y voir !\n\nCordialement,`;
                break;
            default:
                return;
        }

        const encodedSubject = encodeURIComponent(subject);
        const encodedBody = encodeURIComponent(body);
        const mailtoLink = `mailto:?bcc=${emails}&subject=${encodedSubject}&body=${encodedBody}`;

        window.open(mailtoLink);
    };

    const counts = {
        'non envoyé': guestList.filter(g => g.status === 'non envoyé').length,
        'en attente': guestList.filter(g => g.status === 'en attente').length,
        'inscrit': guestList.filter(g => g.status === 'inscrit').length,
        'refus': guestList.filter(g => g.status === 'refus').length
    };

    const renderActionButton = () => {
        switch (currentFilter) {
            case 'non envoyé':
                return <button onClick={handleActionClick} className="bg-[#F2507B] text-white font-bold py-3 px-8 rounded-lg hover:bg-opacity-90 transition-colors">Envoyer l'invitation</button>;
            case 'en attente':
                return <button onClick={handleActionClick} className="bg-blue-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-600 transition-colors">Relancer les invités</button>;
            case 'inscrit':
                return <button onClick={handleActionClick} className="bg-green-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-600 transition-colors">Envoyer un rappel</button>;
            default:
                return null; // No button for 'refus'
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
            <h2 className="text-xl font-bold mb-4">Ajouter des invités</h2>
            <div className="flex flex-col md:flex-row items-start gap-4">
                <textarea value={emailInput} onChange={(e) => setEmailInput(e.target.value)} className="w-full md:flex-grow p-2 border rounded-lg" placeholder="Entrez les mails ici, séparés par une virgule, un point-virgule ou un retour à la ligne..."></textarea>
                <div className="flex flex-col gap-2 w-full md:w-auto">
                    <button onClick={handleValidateClick} className="w-full bg-[#F2507B] text-white font-bold py-2 px-4 rounded-lg hover:bg-opacity-90 transition-colors whitespace-nowrap">Valider la liste</button>
                    <label htmlFor="csv-upload" className="w-full text-center bg-[#22D6B5] text-white font-bold py-2 px-4 rounded-lg hover:bg-opacity-90 transition-colors cursor-pointer whitespace-nowrap">Télécharger un CSV</label>
                    <input type="file" id="csv-upload" accept=".csv,.txt" onChange={handleCsvUpload} className="hidden" />
                </div>
            </div>
            
            <div className="mt-12">
                <h2 className="text-xl font-bold mb-4">Gérer les groupes</h2>
                <div className="flex items-center gap-4 mb-4">
                    <input 
                        type="text" 
                        value={newGroupName} 
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="flex-grow p-2 border rounded-lg"
                        placeholder="Nom du nouveau groupe..."
                    />
                    <input 
                        type="color" 
                        value={newGroupColor}
                        onChange={(e) => setNewGroupColor(e.target.value)}
                        className="w-10 h-10 p-1 border-none rounded-lg cursor-pointer"
                    />
                    <button onClick={handleAddGroup} className="bg-[#22D6B5] text-white font-bold py-2 px-4 rounded-lg hover:bg-opacity-90">Ajouter</button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {groups.map(group => (
                        <div key={group.id} className="bg-gray-200 text-gray-800 text-sm font-medium px-2.5 py-1 rounded-full flex items-center gap-2">
                            <input 
                                type="color" 
                                value={group.color}
                                onChange={(e) => handleGroupColorChange(group.id, e.target.value)}
                                className="w-5 h-5 p-0 border-none rounded-lg cursor-pointer"
                            />
                            <span>{group.name}</span>
                            <button onClick={() => handleDeleteGroup(group.id)} className="text-red-500 hover:text-red-700">×</button>
                        </div>
                    ))}
                </div>
            </div>

            {guestList.length > 0 && (
                <div className="mt-12">
                    <h2 className="text-xl font-bold mb-4">Liste des invités</h2>
                    <div className="flex border-b mb-4 gap-6">
                        {['non envoyé', 'en attente', 'inscrit', 'refus'].map(filter => (
                            <button key={filter} onClick={() => setCurrentFilter(filter)} className={`pb-2 ${currentFilter === filter ? 'text-[#22D6B5] border-b-2 border-[#22D6B5] font-bold' : 'text-gray-500'}`}>
                                {filter.charAt(0).toUpperCase() + filter.slice(1)} ({counts[filter]})
                            </button>
                        ))}
                    </div>

                    {selectedGuests.length > 0 && (
                        <div className="bg-gray-100 p-2 rounded-lg flex items-center gap-4 mb-4">
                            <span className="font-bold text-sm">{selectedGuests.length} invité(s) sélectionné(s)</span>
                            <select 
                                onChange={(e) => handleBulkAssignGroup(e.target.value)} 
                                className="p-1 border rounded-md text-sm"
                                defaultValue=""
                            >
                                <option value="" disabled>Assigner un groupe</option>
                                {groups.map(group => (
                                    <option key={group.id} value={group.id}>{group.name}</option>
                                ))}
                            </select>
                            <button onClick={handleBulkDelete} className="text-red-600 font-semibold text-sm">Supprimer la sélection</button>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-[#22D6B5]/20">
                                <tr>
                                    <th className="py-2 px-4">
                                        <input 
                                            type="checkbox"
                                            checked={filteredList.length > 0 && selectedGuests.length === filteredList.length}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Nom</th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Prénom</th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Fonction</th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Groupe</th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Mail</th>
                                    <th className="py-2 px-4 text-left text-gray-600 font-bold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.length > 0 ? filteredList.map(guest => (
                                    <tr key={guest.email} className="border-b">
                                        <td className="py-2 px-4">
                                            <input 
                                                type="checkbox"
                                                checked={selectedGuests.includes(guest.email)}
                                                onChange={() => handleSelectGuest(guest.email)}
                                            />
                                        </td>
                                        <td className="py-2 px-4">{guest.nom || '-'}</td>
                                        <td className="py-2 px-4">{guest.prenom || '-'}</td>
                                        <td className="py-2 px-4">{guest.fonction || '-'}</td>
                                        <td className="py-2 px-4">
                                            <select value={guest.groupId || ''} onChange={(e) => handleAssignGroup(guest.email, e.target.value)} className="p-1 border rounded-md">
                                                <option value="">Aucun groupe</option>
                                                {groups.map(group => (
                                                    <option key={group.id} value={group.id}>{group.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="py-2 px-4 text-blue-600">{guest.email}</td>
                                        <td className="py-2 px-4">
                                            <button onClick={() => handleDeleteGuest(guest.email)} className="text-red-500 hover:text-red-700">
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7" className="text-center py-4 text-gray-500">Aucun invité ne correspond à ce filtre.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-8 flex justify-end">
                        {renderActionButton()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestManager;